import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import React, { type ReactNode, useMemo } from 'react';

import { DeleteIcon, EditIcon } from '../../../icons';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type IncidentStatus = 'in_progress' | 'resolved';

export function normalizeIncidentStatus(status?: string | null): IncidentStatus {
  if (status === 'resolved' || status === 'closed' || status === 'done') {
    return 'resolved';
  }
  return 'in_progress';
}

export type IncidentDocument = {
  id?: string | null;
  name: string;
  type?: string | null;
  url?: string | null;
  file_url?: string | null;
};

export type IncidentItem = {
  id: string;
  title: string;
  status?: IncidentStatus | string | null;
  date?: string | Date | null;
  providerName?: string | null;
  provider_name?: string | null;
  contactName?: string | null;
  contact_name?: string | null;
  comment?: string | null;
  documents?: IncidentDocument[] | null;
};

export type IncidentCardAction = ResourceMenuItem;

export type IncidentCardProps = {
  incident: IncidentItem;
  documents?: IncidentDocument[];
  maxCommentLines?: number;
  onEdit?: (incident: IncidentItem) => void;
  onDelete?: (incident: IncidentItem) => void;
  onToggleStatus?: (incident: IncidentItem) => void;
  onStatusChange?: (incidentId: string, nextStatus: IncidentStatus) => void;
  onNavigate?: (incident: IncidentItem) => void;
  navigateLabel?: string;
  dragHandle?: ReactNode;
  action?: ReactNode;
  actions?: (IncidentCardAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  footerActions?: ReactNode;
  'data-testid'?: string;
};

export function useIncidentCard(props: IncidentCardProps) {
  const normalizedStatus = normalizeIncidentStatus(props.incident.status);
  const isResolved = normalizedStatus === 'resolved';

  const rawDate = props.incident.date;
  const formattedDate = useMemo(() => {
    if (!rawDate) return null;
    try {
      const dateObj = typeof rawDate === 'string' ? new Date(rawDate) : rawDate;
      if (Number.isNaN(dateObj.getTime())) return null;
      return format(dateObj, 'd MMM yyyy', { locale: fr });
    } catch {
      return null;
    }
  }, [rawDate]);

  const providerName =
    props.incident.providerName?.trim() ||
    props.incident.provider_name?.trim() ||
    null;

  const contactName =
    props.incident.contactName?.trim() ||
    props.incident.contact_name?.trim() ||
    null;

  const hasComment = useMemo(() => {
    if (!props.incident.comment) return false;
    return props.incident.comment.trim().length > 0;
  }, [props.incident.comment]);

  const documents = useMemo(() => {
    const list = props.documents ?? props.incident.documents ?? [];
    return list.filter((doc): doc is IncidentDocument => Boolean(doc && doc.name));
  }, [props.documents, props.incident.documents]);

  function handleEdit() {
    props.onEdit?.(props.incident);
  }

  function handleDelete() {
    props.onDelete?.(props.incident);
  }

  function handleToggleStatus() {
    const nextStatus: IncidentStatus = isResolved ? 'in_progress' : 'resolved';
    props.onToggleStatus?.(props.incident);
    props.onStatusChange?.(props.incident.id, nextStatus);
  }

  function handleNavigate() {
    props.onNavigate?.(props.incident);
  }

  const actions = useMemo(() => {
    const customActions = (props.actions ?? []).filter((action): action is IncidentCardAction => Boolean(action));
    const hasCustomEdit = customActions.some((a) => 'id' in a && a.id === 'edit');
    const hasCustomDelete = customActions.some((a) => 'id' in a && a.id === 'delete');

    const result: IncidentCardAction[] = [];

    if (props.onEdit && !hasCustomEdit) {
      result.push({
        id: 'edit',
        label: 'Modifier',
        icon: React.createElement(EditIcon, { fontSize: 'small' }),
        onClick: handleEdit,
      });
    }

    result.push(...customActions);

    if (props.onDelete && !hasCustomDelete) {
      result.push({
        id: 'delete',
        label: 'Supprimer',
        icon: React.createElement(DeleteIcon, { fontSize: 'small' }),
        onClick: handleDelete,
      });
    }

    return result;
  }, [props.actions, props.onEdit, props.onDelete, props.incident]);

  const hasActions = actions.length > 0;
  const hasFooterActions = Boolean(
    props.footerActions ||
    props.onNavigate ||
    props.onToggleStatus ||
    props.onStatusChange,
  );

  const actionsButtonTooltip = props.actionsButtonTooltip ?? "Options de l'incident";
  const actionsButtonAriaLabel = props.actionsButtonAriaLabel ?? actionsButtonTooltip;

  return {
    normalizedStatus,
    isResolved,
    formattedDate,
    providerName,
    contactName,
    hasComment,
    documents,
    hasDocuments: documents.length > 0,
    actions,
    hasActions,
    hasFooterActions,
    actionsButtonTooltip,
    actionsButtonAriaLabel,
    handleToggleStatus,
    handleNavigate,
  };
}
